'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Context
// =============================================================================

interface RateLimitContextValue {
  /** Report a 429 response so the overlay appears */
  reportRateLimit: () => void;
  /** Whether we are currently rate-limited */
  isRateLimited: boolean;
}

const RateLimitContext = createContext<RateLimitContextValue>({
  reportRateLimit: () => {},
  isRateLimited: false,
});

export const useRateLimit = () => useContext(RateLimitContext);

// =============================================================================
// Re-export the typed 429 error from the framework-free module so existing
// `import { RateLimitError } from '@/providers/rate-limit-provider'` sites
// keep working while server-only fetchers import from `lib/rate-limit-error`
// and don't pull this client-side provider into their bundle.
// =============================================================================

export { RateLimitError } from '@/lib/rate-limit-error';

// =============================================================================
// Provider
// =============================================================================

/** How long to show the overlay after the last 429 (ms) */
const OVERLAY_HOLD_MS = 8_000;
/** Minimum gap between showing the overlay again after it clears */
const COOLDOWN_MS = 2_000;

export function RateLimitProvider({ children }: { children: React.ReactNode }) {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastClearedRef = useRef(0);

  // Clear any pending timers on unmount
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, []);

  const reportRateLimit = useCallback(() => {
    // Debounce — don't re-trigger if we just cleared the overlay
    if (Date.now() - lastClearedRef.current < COOLDOWN_MS && !isRateLimited) {
      return;
    }

    setIsRateLimited(true);

    // Reset the hold timer on every new 429 so the overlay stays visible
    // as long as 429s keep arriving.
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      setIsRateLimited(false);
      lastClearedRef.current = Date.now();
    }, OVERLAY_HOLD_MS);
  }, [isRateLimited]);

  return (
    <RateLimitContext.Provider value={{ reportRateLimit, isRateLimited }}>
      {children}
      <ReconnectingOverlay visible={isRateLimited} />
    </RateLimitContext.Provider>
  );
}

// =============================================================================
// Overlay Component
// =============================================================================

function ReconnectingOverlay({ visible }: { visible: boolean }) {
  // Keep rendered but invisible so the exit animation can play
  const [mounted, setMounted] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  // Grab document.body on the client (SSR-safe)
  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useEffect(() => {
    if (visible) {
      setMounted(true);
    } else {
      // Delay unmount to let fade-out finish
      const t = setTimeout(() => setMounted(false), 500);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!mounted || !portalTarget) return null;

  // Render via portal directly on <body> so we sit above all third-party
  // portals (Privy wallet modals, Radix dialogs, etc.)
  return createPortal(
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center pointer-events-none',
        'transition-opacity duration-500',
        visible ? 'opacity-100' : 'opacity-0',
      )}
      style={{ zIndex: 2147483647 }}
    >
      {/* Backdrop — subtle dark overlay, pointer-events enabled to block interaction */}
      <div
        className={cn(
          'absolute inset-0 bg-background/60 backdrop-blur-sm',
          visible ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      />

      {/* Card */}
      <div
        className={cn(
          'relative z-10 flex flex-col items-center gap-4 px-8 py-6 rounded-2xl',
          'bg-card/95 border border-border/50 shadow-2xl',
          'transition-all duration-500',
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-2',
        )}
      >
        {/* Animated icon */}
        <div className="relative">
          <WifiOff className="h-8 w-8 text-amber-400 animate-reconnect-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-2 border-amber-400/30 animate-reconnect-ring" />
          </div>
        </div>

        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-foreground">
            Reconnecting to network
          </p>
          <p className="text-xs text-muted-foreground">
            Too many requests — retrying automatically...
          </p>
        </div>

        {/* Animated dots */}
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-1.5 w-1.5 rounded-full bg-amber-400"
              style={{
                animation: 'reconnect-dot 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>,
    portalTarget
  );
}
